import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, of, timer } from 'rxjs';
import { debounce, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { CatalogueGame } from 'src/app/models/bot/catalogue';
import { BotResourceService } from 'src/app/services/bot-resource.service';

export interface ShareTemplateDialogData {
  guildId: string;
  templateId: string;
  defaultTitle: string;
}

@Component({
  selector: 'app-share-template-dialog',
  templateUrl: './share-template-dialog.component.html',
  styleUrls: ['./share-template-dialog.component.css'],
})
export class ShareTemplateDialogComponent implements OnInit {
  form: FormGroup;
  gameControl = new FormControl('');
  games: CatalogueGame[] = [];
  filteredGames$: Observable<CatalogueGame[]> = of([]);
  selectedGame: CatalogueGame | null = null;
  createNewGame = false;
  submitting = false;

  constructor(
    private fb: FormBuilder,
    private botService: BotResourceService,
    private dialogRef: MatDialogRef<ShareTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareTemplateDialogData
  ) {
    this.form = this.fb.group({
      title: [data.defaultTitle || '', [Validators.required, Validators.minLength(2), Validators.maxLength(25)]],
      description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(1024)]],
      notes: ['', [Validators.maxLength(1024)]],
    });
  }

  private isGameObject(value: unknown): value is CatalogueGame {
    return !!value && typeof value === 'object' && 'name' in (value as object) && 'id' in (value as object);
  }

  get pendingAddLabel(): string {
    const raw = this.gameControl.value;
    if (this.isGameObject(raw)) {
      return '';
    }
    return (raw ?? '').toString().trim();
  }

  get showAddOption(): boolean {
    const label = this.pendingAddLabel;
    if (!label) {
      return false;
    }
    return !this.games.some((g) => g.name.toLowerCase() === label.toLowerCase());
  }

  ngOnInit(): void {
    this.botService.listCatalogueGames().subscribe((games) => {
      this.games = games;
    });
    this.filteredGames$ = this.gameControl.valueChanges.pipe(
      startWith(''),
      map((value) => (this.isGameObject(value) ? value.name : (value ?? '').toString())),
      debounce((query) => timer(query.trim() ? 250 : 0)),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query.trim()) {
          return of(this.games);
        }
        return this.botService.listCatalogueGames(query);
      })
    );
  }

  displayGame(game: CatalogueGame | string | null): string {
    if (!game) {
      return '';
    }
    return typeof game === 'string' ? game : game.name;
  }

  onAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    if (value && typeof value === 'object' && 'id' in value) {
      this.selectedGame = value as CatalogueGame;
      this.createNewGame = false;
      this.gameControl.setValue((value as CatalogueGame).name);
      return;
    }
    const name = (value || '').toString().trim();
    this.selectedGame = null;
    this.createNewGame = !!name;
    this.gameControl.setValue(name);
  }

  onGameBlur(): void {
    const raw = this.gameControl.value;
    if (this.isGameObject(raw)) {
      this.selectedGame = raw;
      this.createNewGame = false;
      return;
    }
    const name = (raw ?? '').toString().trim();
    if (!name) {
      this.selectedGame = null;
      this.createNewGame = false;
      return;
    }
    const match = this.games.find((g) => g.name.toLowerCase() === name.toLowerCase());
    if (match) {
      this.selectedGame = match;
      this.createNewGame = false;
    } else {
      this.selectedGame = null;
      this.createNewGame = true;
    }
  }

  submit(): void {
    this.onGameBlur();
    if (this.form.invalid || (!this.selectedGame && !this.createNewGame)) {
      this.form.markAllAsTouched();
      this.gameControl.markAsTouched();
      return;
    }
    const rawGame =
      this.selectedGame?.name ||
      (typeof this.gameControl.value === 'string'
        ? this.gameControl.value.trim()
        : (this.gameControl.value as CatalogueGame)?.name || '');
    this.submitting = true;
    this.botService
      .shareSignupTemplate(this.data.guildId, this.data.templateId, {
        title: this.form.value.title,
        description: this.form.value.description,
        notes: this.form.value.notes || '',
        game_id: this.selectedGame ? this.selectedGame.id : null,
        pending_game_name: this.createNewGame ? rawGame : null,
      })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.dialogRef.close(true);
        },
        error: () => {
          this.submitting = false;
        },
      });
  }
}
